import { useState, useEffect, useRef } from 'react';
import { ERROR_MESSAGES, geocodeLocation } from '../utils/zipcodeHelper';
import { fetchPlaceLocation } from '../utils/locationHelper';

/**
 * Fetches the zipcode for a given Google Place ID using the PlacesService.
 * This hook is intended to be used after a user selects a location from
 * autocomplete suggestions, providing a placeId.
 *
 * @param {boolean} isApiReady - A flag indicating if the Google Maps API script has loaded.
 * @param {object | null} sessionToken - The active AutocompleteSessionToken from the search session.
 * @param {Function} resetSession - The function to reset/end the session after use.
 * @param {string | null} placeIdToFetch - The Google Place ID for which to fetch the zipcode.
 */
export const useFetchLocationGeoZip = (
	isApiReady,
	sessionToken,
	resetSession,
	placeIdToFetch
) => {
	const [zipcode, setZipcode] = useState(null);
	const [error, setError] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	const sessionTokenRef = useRef(sessionToken);
	useEffect(() => {
		// Keep a ref to the latest session token to ensure it's available in async calls without needing to add it to dependencies
		sessionTokenRef.current = sessionToken;
	}, [sessionToken]);

	useEffect(() => {
		if (!placeIdToFetch || !isApiReady) {
			setZipcode(null);
			setError(null);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);

		const fetchZipcode = async () => {
			try {
				// Step 1: Get coordinates from place ID
				const { lat, lng } = await fetchPlaceLocation(
					placeIdToFetch,
					sessionTokenRef.current
				);

				// Step 2: Geocode coordinates to get zipcode
				const fetchedZipcode = await geocodeLocation(lat, lng);

				setZipcode(fetchedZipcode);
				setError(null);
			} catch (err) {
				setError(err.message || ERROR_MESSAGES.FETCH_FAILED);
				setZipcode(null);
			} finally {
				// Always reset session after fetch attempt
				resetSession?.();
				setIsLoading(false);
			}
		};

		fetchZipcode();
	}, [placeIdToFetch, isApiReady, resetSession]);

	return { zipcode, isLoading, error };
};
